/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import { position } from "stylis";

function DataTableHeadCell({
  width,
  children,
  sorted,
  align,
  sticky,
  className,
  ...rest
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // map 'true' => 'left' nếu sau dùng boolean
  const stickySide = sticky === true ? "left" : sticky;
  const stickySx = stickySide
    ? {
        position: "sticky",
        [stickySide]: 0, // left: 0 hoặc right: 0
        zIndex: 3, // cao hơn body cell
        // viền nhẹ để phân tách cột dính
        backgroundColor: "#f7f9fb",
      }
    : {};

  return (
    <MDBox
      component="th"
      width={width}
      py={1}
      px={2}
      sx={({ palette: { light }, borders: { borderWidth } }) => ({
        borderBottom: `${borderWidth[1]} solid ${light.main}`,
        position: "sticky",
        top: "0",
        backgroundColor: "#f7f9fb",
        ...stickySx, // <— thêm dòng này
      })}
      className={[
        "cellTh",
        className,
        sticky && "sticky",
        sticky === "left" && "stickyLeft stickyTh",
        sticky === "right" && "stickyRight stickyTh",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <MDBox
        {...rest}
        position="relative"
        textAlign={align}
        color={darkMode ? "white" : "secondary"}
        opacity={0.7}
        sx={({ typography: { size, fontWeightBold } }) => ({
          fontSize: size.xxs,
          fontWeight: fontWeightBold,
          textTransform: "uppercase",
          cursor: sorted && "pointer",
          userSelect: sorted && "none",
        })}
      >
        {children}
        {sorted && (
          <MDBox
            position="absolute"
            top={0}
            right={align !== "right" ? "16px" : 0}
            left={align === "right" ? "-5px" : "unset"}
            sx={({ typography: { size } }) => ({ fontSize: size.lg })}
          >
            <MDBox
              position="absolute"
              top={-6}
              color={sorted === "asce" ? "text" : "secondary"}
              opacity={sorted === "asce" ? 1 : 0.5}
            >
              <Icon>arrow_drop_up</Icon>
            </MDBox>
            <MDBox
              position="absolute"
              top={0}
              color={sorted === "desc" ? "text" : "secondary"}
              opacity={sorted === "desc" ? 1 : 0.5}
            >
              <Icon>arrow_drop_down</Icon>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of DataTableHeadCell
DataTableHeadCell.defaultProps = {
  width: "auto",
  sorted: "none",
  align: "left",
  sticky: null,
};

// Typechecking props for the DataTableHeadCell
DataTableHeadCell.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  sorted: PropTypes.oneOf([false, "none", "asce", "desc"]),
  align: PropTypes.oneOf(["left", "right", "center"]),
  sticky: PropTypes.oneOf(["left", "right", null]),
};

export default DataTableHeadCell;
